"use client";

import type { DispatchRecipient, NoneOffered } from "@/utils/dispatchControl";
import React from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fetchCitiesByZip } from "@/api/simpego";
import { ZipCodeCity } from "@/types/simpego-api";
import { useTranslation } from "react-i18next";
import { useAgentId } from "@/context/AgentIdContext";
import GenderBadges from "@/components/contact-details/GenderBadges";
import NameFields from "@/components/contact-details/NameFields";
import EmailPhoneFields from "@/components/contact-details/EmailPhoneFields";
import StreetHouseFields from "@/components/contact-details/StreetHouseFields";
import ZipCityFields from "@/components/contact-details/ZipCityFields";
import BirthdateField from "@/components/contact-details/BirthdateField";
import CampaignCodeField, { isValidCampaignCode } from "@/components/contact-details/CampaignCodeField";
import ApplicationQuestionsSection, { ApplicationAnswers, EMPTY_APPLICATION_ANSWERS } from "@/components/contact-details/ApplicationQuestionsSection";
import DispatchControlSection from "@/components/contact-details/DispatchControlSection";
import ConsultationProtocolUpload from "@/components/contact-details/ConsultationProtocolUpload";
import { useErrorDialog } from "@/context/ErrorDialogContext";

interface ContactDetailsFormProps {
  onContinueToSummary: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  /** Fired whenever a field loses focus, so typed-but-unsubmitted contact data survives
   * a failed submission or the customer navigating away and back. */
  onFieldBlur?: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  onBack: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialPhone?: string;
  initialStreet?: string;
  initialHousenumber?: string;
  initialCity?: string;
  initialZipCode?: string;
  initialBirthdate?: Date;
  initialGender?: "male" | "female" | "";
  isLoading?: boolean;
  initialDispatchContract?: DispatchRecipient;
  initialDispatchInvoice?: DispatchRecipient;
  initialDispatchReminder?: DispatchRecipient;
  initialDispatchEmail?: DispatchRecipient;
  initialAgentDispatchEmail?: string;
  dispatchNoneOffered?: NoneOffered;
  initialCampaignCode?: string;
  petName?: string;
  onCampaignCodeChange?: (code: string) => void;
  applicationAnswers?: ApplicationAnswers;
  onApplicationAnswersChange?: (answers: ApplicationAnswers) => void;
  onDispatchContractChange?: (v: DispatchRecipient) => void;
  onDispatchInvoiceChange?: (v: DispatchRecipient) => void;
  onDispatchReminderChange?: (v: DispatchRecipient) => void;
  onDispatchEmailChange?: (v: DispatchRecipient) => void;
  onAgentDispatchEmailChange?: (email: string | null) => void;
  underwritingFailed?: boolean;
  /** Beratungsprotokoll upload (agents only) — the file is sent along with the offer created on submit. */
  consultationProtocol?: React.ComponentProps<typeof ConsultationProtocolUpload>;
}

const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  onContinueToSummary,
  onFieldBlur,
  onBack,
  initialFirstName = "",
  initialLastName = "",
  initialEmail = "",
  initialPhone = "",
  initialStreet = "",
  initialHousenumber = "",
  initialCity = "",
  initialZipCode = "",
  initialBirthdate,
  initialGender = "",
  isLoading = false,
  initialDispatchContract = "customer",
  initialDispatchInvoice = "customer",
  initialDispatchReminder = "customer",
  initialDispatchEmail = "agent",
  initialAgentDispatchEmail = "",
  dispatchNoneOffered,
  initialCampaignCode = "",
  petName = "",
  onCampaignCodeChange,
  applicationAnswers = EMPTY_APPLICATION_ANSWERS,
  onApplicationAnswersChange,
  onDispatchContractChange,
  onDispatchInvoiceChange,
  onDispatchReminderChange,
  onDispatchEmailChange,
  onAgentDispatchEmailChange,
  underwritingFailed = false,
  consultationProtocol,
}) => {
  const [firstName, setFirstName] = React.useState(initialFirstName);
  const [lastName, setLastName] = React.useState(initialLastName);
  const [email, setEmail] = React.useState(initialEmail);
  const [phone, setPhone] = React.useState(initialPhone);
  const [street, setStreet] = React.useState(initialStreet);
  const [housenumber, setHousenumber] = React.useState(initialHousenumber);
  const [zipCode, setZipCode] = React.useState(initialZipCode);
  const [city, setCity] = React.useState(initialCity);
  const [showCityInput, setShowCityInput] = React.useState(!!initialCity);
  const [cityOptions, setCityOptions] = React.useState<string[]>([]);
  const [isFetchingCity, setIsFetchingCity] = React.useState(false);
  const country = "CH";
  const [birthdate, setBirthdate] = React.useState<Date | undefined>(
    initialBirthdate ? new Date(initialBirthdate) : undefined
  );
  const [gender, setGender] = React.useState<"male" | "female" | "">(initialGender || "");

  const [dispatchContract, setDispatchContractLocal] = React.useState<DispatchRecipient>(initialDispatchContract);
  const [dispatchInvoice, setDispatchInvoiceLocal] = React.useState<DispatchRecipient>(initialDispatchInvoice);
  const [dispatchReminder, setDispatchReminderLocal] = React.useState<DispatchRecipient>(initialDispatchReminder);
  const [dispatchEmail, setDispatchEmailLocal] = React.useState<DispatchRecipient>(initialDispatchEmail);
  const [campaignCode, setCampaignCodeLocal] = React.useState<string>(initialCampaignCode);
  // Antragsfragen: "" = not answered yet. Held in the funnel state so the answers survive stepping forward or back.
  const { termination: terminationAnswer, owner: ownerAnswer } = applicationAnswers;
  const [agentDispatchEmail, setAgentDispatchEmailLocal] = React.useState<string>(initialAgentDispatchEmail);

  const { agentId, agentEmails, isCustomAgentId, agentToken } = useAgentId();
  const { showErrorDialog } = useErrorDialog();

  const firstNameInputRef = React.useRef<HTMLInputElement>(null);
  const lastNameInputRef = React.useRef<HTMLInputElement>(null);
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const phoneInputRef = React.useRef<HTMLInputElement>(null);
  const streetInputRef = React.useRef<HTMLInputElement>(null);
  const housenumberInputRef = React.useRef<HTMLInputElement>(null);
  const cityInputRef = React.useRef<HTMLDivElement>(null);
  const zipCodeInputRef = React.useRef<HTMLInputElement>(null);
  const genderGroupRef = React.useRef<HTMLDivElement>(null);
  const birthdateGroupRef = React.useRef<HTMLDivElement>(null);
  const agentDispatchEmailRef = React.useRef<HTMLDivElement>(null); // New ref for agent email
  const applicationQuestionsRef = React.useRef<HTMLDivElement>(null);
  const campaignCodeRef = React.useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation();

  const [errors, setErrors] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    housenumber: "",
    city: "",
    zipCode: "",
    birthdate: "",
    gender: "",
    agentDispatchEmail: "", // New error state
    terminationAnswer: "",
    ownerAnswer: "",
    campaignCode: "",
  });

  const isAgentAuthenticated = !!agentToken;
  const isBrokerFunnel = !!agentId && isCustomAgentId;
  const agentEmailWarningMessage =
    isAgentAuthenticated &&
    agentDispatchEmail &&
    email &&
    agentDispatchEmail.toLowerCase() === email.toLowerCase()
      ? t("contactDetails.agentEmailSameAsCustomerWarning")
      : "";

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateEmail = (emailValue: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  const validatePhone = (phoneValue: string) => /^\+?[0-9]{10,15}$/.test(phoneValue) || phoneValue === "";

  const fetchCityForZip = React.useCallback(async (zip: string) => {
    if (zip.length === 4 && /^\d{4}$/.test(zip)) {
      setIsFetchingCity(true);
      setErrors(prev => ({ ...prev, zipCode: "" }));
      try {
        const cities: ZipCodeCity[] = await fetchCitiesByZip(zip, i18n.language);
        if (cities && cities.length > 0) {
          setCityOptions(cities.map((c) => c.name));
          setCity(cities.length === 1 ? cities[0].name : "");
          setShowCityInput(true);
        } else {
          setCityOptions([]);
          setCity("");
          setShowCityInput(true);
          setErrors(prev => ({ ...prev, city: t("contactDetails.validation.noCityFound") }));
        }
      } catch (error) {
        console.error("Error fetching city for zip:", error);
        setCityOptions([]);
        setCity("");
        setShowCityInput(true);
        setErrors(prev => ({ ...prev, city: t("contactDetails.validation.errorFetchingCity") }));
      } finally {
        setIsFetchingCity(false);
      }
    } else {
      setCity("");
      setCityOptions([]);
      setShowCityInput(false);
      setErrors(prev => ({ ...prev, city: "" }));
    }
  }, [t, i18n.language, showErrorDialog]);

  // Update local state when initial props change
  React.useEffect(() => { setFirstName(initialFirstName); }, [initialFirstName]);
  React.useEffect(() => { setLastName(initialLastName); }, [initialLastName]);
  React.useEffect(() => { setEmail(initialEmail); }, [initialEmail]);
  React.useEffect(() => { setPhone(initialPhone); }, [initialPhone]);
  React.useEffect(() => { setStreet(initialStreet); }, [initialStreet]);
  React.useEffect(() => { setHousenumber(initialHousenumber); }, [initialHousenumber]);
  React.useEffect(() => { setCity(initialCity); setShowCityInput(!!initialCity); }, [initialCity]);
  React.useEffect(() => { setZipCode(initialZipCode); }, [initialZipCode]);
  React.useEffect(() => { setBirthdate(initialBirthdate ? new Date(initialBirthdate) : undefined); }, [initialBirthdate]);
  React.useEffect(() => { setGender(initialGender || ""); }, [initialGender]);
  React.useEffect(() => { setDispatchContractLocal(initialDispatchContract); }, [initialDispatchContract]);
  React.useEffect(() => { setDispatchInvoiceLocal(initialDispatchInvoice); }, [initialDispatchInvoice]);
  React.useEffect(() => { setDispatchReminderLocal(initialDispatchReminder); }, [initialDispatchReminder]);
  React.useEffect(() => { setDispatchEmailLocal(initialDispatchEmail); }, [initialDispatchEmail]);
  React.useEffect(() => { setAgentDispatchEmailLocal(initialAgentDispatchEmail); }, [initialAgentDispatchEmail]);
  React.useEffect(() => { setCampaignCodeLocal(initialCampaignCode); }, [initialCampaignCode]);


  React.useEffect(() => {
    if (zipCode.length === 4 && /^\d{4}$/.test(zipCode) && city === "" && cityOptions.length === 0) { // Only fetch if zip is valid and no city/options are set yet
      fetchCityForZip(zipCode);
    } else if (zipCode.length !== 4) {
      setCity("");
      setCityOptions([]);
      setShowCityInput(false);
    }
  }, [zipCode, fetchCityForZip, city, cityOptions.length]); // Depend on local zipCode state

  const handleZipCodeChange = (newZip: string) => {
    setZipCode(newZip);
    setCity("");
    setCityOptions([]);
    setErrors(prev => ({ ...prev, zipCode: "", city: "" }));
    // The useEffect above will handle fetching the city based on the new zipCode state
  };

  // React's onBlur bubbles (via focusout), so attaching this once on the fields
  // container persists the current draft whenever any field loses focus.
  const handleFieldBlur = () => {
    onFieldBlur?.(firstName, lastName, email, phone, street, housenumber, city, zipCode, country, birthdate, gender);
  };

  const validateForm = () => {
    let currentErrors = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      street: "",
      housenumber: "",
      city: "",
      zipCode: "",
      birthdate: "",
      gender: "",
      agentDispatchEmail: "", // Initialize new error field
      terminationAnswer: "",
      ownerAnswer: "",
      campaignCode: "",
    };
    let hasError = false;

    if (!firstName.trim()) {
      currentErrors.firstName = t("contactDetails.validation.enterFirstName");
      hasError = true;
    }
    if (!lastName.trim()) {
      currentErrors.lastName = t("contactDetails.validation.enterLastName");
      hasError = true;
    }
    if (!validateEmail(email)) {
      currentErrors.email = t("contactDetails.validation.validEmail");
      hasError = true;
    }
    if (!validatePhone(phone)) {
      currentErrors.phone = t("contactDetails.validation.validPhone");
      hasError = true;
    }
    if (!street.trim()) {
      currentErrors.street = t("contactDetails.validation.enterStreet");
      hasError = true;
    }
    if (!housenumber.trim()) {
      currentErrors.housenumber = t("contactDetails.validation.enterHousenumber");
      hasError = true;
    }
    if (!zipCode.trim() || !/^\d{4}$/.test(zipCode)) {
      currentErrors.zipCode = t("contactDetails.validation.validZipCode");
      hasError = true;
    }
    if (showCityInput && !city.trim()) {
      currentErrors.city = t("contactDetails.validation.enterCity");
      hasError = true;
    }
    if (!birthdate) {
      currentErrors.birthdate = t("contactDetails.validation.enterBirthDate");
      hasError = true;
    } else {
      const bd = birthdate;
      const today = new Date();
      let age = today.getFullYear() - bd.getFullYear();
      const mdiff = today.getMonth() - bd.getMonth();
      if (mdiff < 0 || (mdiff === 0 && today.getDate() < bd.getDate())) age--;
      if (age < 18) {
        currentErrors.birthdate = t("contactDetails.validation.tooYoung");
        hasError = true;
      }
    }

    if (!gender) {
      currentErrors.gender = t("contactDetails.validation.enterGender");
      hasError = true;
    }

    // New validation for agentDispatchEmail
    if (isCustomAgentId && agentEmails && agentEmails.length > 0 && !agentDispatchEmail.trim()) {
      currentErrors.agentDispatchEmail = t("contactDetails.validation.enterAgentEmail"); // Assuming this translation key exists
      hasError = true;
    }

    // A customer whose application was rejected or terminated before, or who is not the owner/keeper, cannot take out the insurance.
    // Like all other fields this is only checked on "Weiter".
    if (!terminationAnswer) {
      currentErrors.terminationAnswer = t("contactDetails.applicationQuestions.required");
      hasError = true;
    } else if (terminationAnswer === "yes") {
      currentErrors.terminationAnswer = t("contactDetails.applicationQuestions.notEligible");
      hasError = true;
    }
    if (!ownerAnswer) {
      currentErrors.ownerAnswer = t("contactDetails.applicationQuestions.required");
      hasError = true;
    } else if (ownerAnswer === "no") {
      currentErrors.ownerAnswer = t("contactDetails.applicationQuestions.notEligible");
      hasError = true;
    }
    if (!isBrokerFunnel && campaignCode && !isValidCampaignCode(campaignCode)) {
      currentErrors.campaignCode = t("contactDetails.campaignCodeInvalid");
      hasError = true;
    }

    setErrors(currentErrors); // Update state
    return { currentErrors, hasError }; // Return for immediate use
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleContinue();
  };

  const handleContinue = () => {
    const { currentErrors, hasError } = validateForm(); // Get immediate validation results

    if (hasError) {
      const errorRefs = [
        { ref: agentDispatchEmailRef, error: currentErrors.agentDispatchEmail }, // Prioritize agent email error
        { ref: genderGroupRef, error: currentErrors.gender },
        { ref: firstNameInputRef, error: currentErrors.firstName },
        { ref: lastNameInputRef, error: currentErrors.lastName },
        { ref: emailInputRef, error: currentErrors.email },
        { ref: phoneInputRef, error: currentErrors.phone },
        { ref: streetInputRef, error: currentErrors.street },
        { ref: housenumberInputRef, error: currentErrors.housenumber },
        { ref: zipCodeInputRef, error: currentErrors.zipCode },
        { ref: cityInputRef, error: currentErrors.city },
        { ref: birthdateGroupRef, error: currentErrors.birthdate },
        { ref: applicationQuestionsRef, error: currentErrors.terminationAnswer || currentErrors.ownerAnswer },
        { ref: campaignCodeRef, error: currentErrors.campaignCode },
      ];
      for (const { ref, error } of errorRefs) {
        if (error && ref.current) {
          ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        }
      }
      return;
    }

    onContinueToSummary(
      firstName,
      lastName,
      email,
      phone,
      street,
      housenumber,
      city,
      zipCode,
      country,
      birthdate,
      gender
    );
  };

  React.useEffect(() => {
    onDispatchContractChange?.(dispatchContract);
  }, [dispatchContract, onDispatchContractChange]);

  React.useEffect(() => {
    onDispatchInvoiceChange?.(dispatchInvoice);
  }, [dispatchInvoice, onDispatchInvoiceChange]);

  React.useEffect(() => {
    // The broker form has no reminders field: reminders follow the Inkasso setting.
    onDispatchReminderChange?.(isBrokerFunnel ? dispatchInvoice : dispatchReminder);
  }, [isBrokerFunnel, dispatchInvoice, dispatchReminder, onDispatchReminderChange]);

  React.useEffect(() => {
    onDispatchEmailChange?.(dispatchEmail);
  }, [dispatchEmail, onDispatchEmailChange]);

  React.useEffect(() => {
    onAgentDispatchEmailChange?.(agentDispatchEmail ? agentDispatchEmail : null);
  }, [agentDispatchEmail, onAgentDispatchEmailChange]);

  return (
    <Card className="w-full max-w-4xl mx-auto border-none shadow-none">
      {/* A real <form> (with its own validation, hence noValidate) so browsers offer autofill and can save the data. */}
      <form onSubmit={handleSubmit} noValidate>
      {!isBrokerFunnel && (
        <CardHeader>
          <CardTitle className="text-2xl font-bold">{t("banner-header-3")}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={isBrokerFunnel ? "space-y-4 pt-6" : "space-y-4"} onBlur={handleFieldBlur}>
        {isBrokerFunnel && (
          <DispatchControlSection
            dispatchContract={dispatchContract}
            dispatchInvoice={dispatchInvoice}
            dispatchEmail={dispatchEmail}
            agentEmails={agentEmails || []}
            agentDispatchEmail={agentDispatchEmail}
            onDispatchContractChange={(v) => setDispatchContractLocal(v)}
            onDispatchInvoiceChange={(v) => setDispatchInvoiceLocal(v)}
            onDispatchEmailChange={(v) => setDispatchEmailLocal(v)}
            onAgentDispatchEmailChange={(email) => {
              setAgentDispatchEmailLocal(email);
              setErrors(prev => ({ ...prev, agentDispatchEmail: "" })); // Clear error on change
            }}
            agentDispatchEmailError={errors.agentDispatchEmail} // Pass error
            agentDispatchEmailRef={agentDispatchEmailRef} // Pass ref
            noneOffered={dispatchNoneOffered}
          />
        )}

        {isBrokerFunnel && <h3 className="pt-2 text-xl font-semibold">{t("contactDetails.customerDataTitle")}</h3>}

        <div ref={genderGroupRef}>
          <GenderBadges
            value={gender}
            onChange={(v) => {
              setGender(v);
              setErrors(prev => ({ ...prev, gender: "" }));
            }}
          />
          {errors.gender && <p className="text-sm text-destructive">{errors.gender}</p>}
        </div>

        <NameFields
          firstName={firstName}
          lastName={lastName}
          errors={{ firstName: errors.firstName, lastName: errors.lastName }}
          disabled={isLoading}
          onFirstNameChange={(v) => {
            setFirstName(v);
            setErrors(prev => ({ ...prev, firstName: "" }));
          }}
          onLastNameChange={(v) => {
            setLastName(v);
            setErrors(prev => ({ ...prev, lastName: "" }));
          }}
          firstNameRef={firstNameInputRef}
          lastNameRef={lastNameInputRef}
        />

        <EmailPhoneFields
          email={email}
          phone={phone}
          errors={{ email: errors.email, phone: errors.phone }}
          disabled={isLoading}
          onEmailChange={(v) => {
            setEmail(v);
            setErrors(prev => ({ ...prev, email: "" }));
          }}
          onPhoneChange={(v) => {
            setPhone(v);
            setErrors(prev => ({ ...prev, phone: "" }));
          }}
          warningMessage={agentEmailWarningMessage}
          emailRef={emailInputRef}
          phoneRef={phoneInputRef}
        />

        <StreetHouseFields
          street={street}
          housenumber={housenumber}
          errors={{ street: errors.street, housenumber: errors.housenumber }}
          disabled={isLoading}
          onStreetChange={(v) => {
            setStreet(v);
            setErrors(prev => ({ ...prev, street: "" }));
          }}
          onHousenumberChange={(v) => {
            setHousenumber(v);
            setErrors(prev => ({ ...prev, housenumber: "" }));
          }}
          streetRef={streetInputRef}
          housenumberRef={housenumberInputRef}
        />

        <ZipCityFields
          zipCode={zipCode}
          city={city}
          cityOptions={cityOptions}
          showCityInput={showCityInput}
          isFetchingCity={isFetchingCity}
          errors={{ zipCode: errors.zipCode, city: errors.city }}
          disabled={isLoading}
          onZipCodeChange={(v) => handleZipCodeChange(v)}
          onCityChange={(v) => {
            setCity(v);
            setErrors(prev => ({ ...prev, city: "" }));
          }}
          zipCodeRef={zipCodeInputRef}
          cityRef={cityInputRef}
        />

        <div ref={birthdateGroupRef}>
          <BirthdateField
            birthdate={birthdate}
            error={errors.birthdate}
            disabled={isLoading}
            onBirthdateChange={(d) => {
              setBirthdate(d);
              setErrors(prev => ({ ...prev, birthdate: "" }));
            }}
          />
        </div>

        {!isBrokerFunnel && (
          <div ref={campaignCodeRef} className="border-y border-gray-200 py-4">
            <CampaignCodeField
              value={campaignCode}
              disabled={isLoading}
              error={errors.campaignCode}
              onChange={(v) => {
                setCampaignCodeLocal(v);
                setErrors(prev => ({ ...prev, campaignCode: "" }));
                onCampaignCodeChange?.(v);
              }}
            />
          </div>
        )}

        <div ref={applicationQuestionsRef}>
          <ApplicationQuestionsSection
            petName={petName}
            isBroker={isBrokerFunnel}
            terminationAnswer={terminationAnswer}
            ownerAnswer={ownerAnswer}
            terminationError={errors.terminationAnswer}
            ownerError={errors.ownerAnswer}
            disabled={isLoading}
            onTerminationChange={(v) => {
              onApplicationAnswersChange?.({ ...applicationAnswers, termination: v });
              setErrors(prev => ({ ...prev, terminationAnswer: "" }));
            }}
            onOwnerChange={(v) => {
              onApplicationAnswersChange?.({ ...applicationAnswers, owner: v });
              setErrors(prev => ({ ...prev, ownerAnswer: "" }));
            }}
          />
        </div>

        {isAgentAuthenticated && consultationProtocol && (
          <ConsultationProtocolUpload {...consultationProtocol} />
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground text-center">
          {t("contactDetails.privacyConsentPrefix")}{" "}
          <a
            href={t("common.privacyPolicyLink") as string}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-4 hover:opacity-80"
          >
            {t("common.privacyPolicy")}
          </a>
          {t("contactDetails.privacyConsentPostfix")}
        </p>
        <div className="flex w-full flex-col-reverse gap-3 md:flex-row md:gap-4">
          <Button type="button" variant="outline" onClick={onBack} className="w-full h-12 text-lg font-bold" disabled={isLoading || isFetchingCity}>
            {t("contactDetails.back")}
          </Button>
          <Button
            type="submit"
            className="w-full h-12 text-lg font-bold bg-continue-button text-continue-button-foreground hover:bg-continue-button/90"
            disabled={isLoading || isFetchingCity || underwritingFailed}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("contactDetails.continue")}
          </Button>
        </div>
      </CardFooter>
      </form>
    </Card>
  );
};

export default ContactDetailsForm;
